<template>
  <!-- An iOS segmented control: a grey track, the chosen segment a white thumb.
       A radiogroup with one tab stop; the arrow keys move and choose. With
       nothing chosen (a saved ask carrying a value the control does not offer)
       the first segment takes the tab stop. -->
  <div
    class="segmented"
    role="radiogroup"
    :aria-label="label || undefined"
    :aria-labelledby="labelledby || undefined"
    @keydown="radioKeys"
  >
    <button
      v-for="(o, i) in options"
      :key="String(o.value)"
      type="button"
      role="radio"
      class="segment"
      :aria-checked="modelValue === o.value ? 'true' : 'false'"
      :aria-label="o.aria || undefined"
      :tabindex="modelValue === o.value || (!hasChoice && i === 0) ? 0 : -1"
      @click="emit('update:modelValue', o.value)"
    >{{ o.label }}</button>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { radioKeys } from '../../lib/ask/radio-keys.js';

const props = defineProps({
  options: { type: Array, required: true },
  modelValue: { type: [String, Number, null], default: null },
  label: { type: String, default: '' },
  labelledby: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);
const hasChoice = computed(() => props.options.some((o) => o.value === props.modelValue));
</script>

<style scoped>
/* The track is the 44px target itself; the thumb is drawn 2px inside it, so the
   control stays 44 tall and every segment is a full 44px target. */
.segmented {
  display: flex;
  min-width: 0;
  border-radius: 10px;
  background: var(--fill, var(--rule));
}
.segment {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  min-height: 44px;
  padding: 0 8px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--carbon);
  font: 600 0.8125rem/1 var(--font-body);
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  isolation: isolate;
}
.segment::before {
  content: '';
  position: absolute;
  z-index: -1;
  inset: 2px;
  border-radius: 8px;
  background: transparent;
}
.segment[aria-checked='true']::before {
  background: var(--sheet);
  box-shadow: 0 1px 3px color-mix(in srgb, var(--carbon) 16%, transparent);
}
.segment:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}
</style>
